import React, { useEffect, useMemo, useState } from "react";
import styles from "../topic.module.css";

import { api } from "../../api/api";
import { normalizeToMapPoints } from "../../services/mapAdapter";
import { useModuleContext } from "../ModuleContext";
import { cachedLoad } from "../cache";
import {
  SideList,
  SideListDateHeader,
  SideListGroup,
  SideListItem,
  SideListMessage,
} from "../SideList";

// selectable time windows, in days from today (0 = today only)
const RANGES = [
  { days: 0, label: { de: "Heute", en: "Today" } },
  { days: 6, label: { de: "7 Tage", en: "7 days" } },
  { days: 29, label: { de: "30 Tage", en: "30 days" } },
];

const TEXT = {
  empty: { de: "Keine Veranstaltungen in diesem Zeitraum.", en: "No events in this period." },
  cancelled: { de: "abgesagt", en: "cancelled" },
  today: { de: "Heute", en: "Today" },
  tomorrow: { de: "Morgen", en: "Tomorrow" },
  allDay: { de: "ganztägig", en: "all day" },
  until: { de: "bis", en: "until" },
  count: {
    de: (n) => (n === 1 ? "1 Termin" : `${n} Termine`),
    en: (n) => (n === 1 ? "1 event" : `${n} events`),
  },
};
const t = (entry, language) => entry[language] ?? entry.de;

// local calendar date as YYYY-MM-DD (toISOString would use UTC)
function isoDate(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function formatDay(isoDay, language) {
  return new Date(`${isoDay}T00:00`).toLocaleDateString(language, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

// pieces for the calendar date tile + label ("Heute", "Morgen", "Freitag")
function dayHeaderParts(isoDay, language) {
  const date = new Date(`${isoDay}T00:00`);
  const fmt = (options) => date.toLocaleDateString(language, options).replace(".", "");

  const now = new Date();
  const today = isoDate(now);
  // calendar day + 1 (not "now + 24 h", which is off on DST change days)
  const tomorrow = isoDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));
  const label =
    isoDay === today
      ? t(TEXT.today, language)
      : isoDay === tomorrow
        ? t(TEXT.tomorrow, language)
        : date.toLocaleDateString(language, { weekday: "long" });

  return {
    weekday: fmt({ weekday: "short" }),
    day: date.getDate(),
    month: fmt({ month: "short" }),
    label,
  };
}

// left column of an event card: start time (+ "bis 21:00"), or "ganztägig"
function timeAside(occ, language) {
  if (!occ.start_time) return <small>{t(TEXT.allDay, language)}</small>;
  return (
    <>
      {occ.start_time}
      {occ.end_time && (
        <small>
          {t(TEXT.until, language)} {occ.end_time}
        </small>
      )}
    </>
  );
}

function formatTime(occ) {
  if (!occ.start_time) return "";
  return occ.end_time ? `${occ.start_time}–${occ.end_time}` : occ.start_time;
}

// one event can occur on several days (and several times a day)
const occurrenceKey = (o) => `event-${o.event_id}-${o.date}-${o.start_time}`;

// occurrence -> shape used by PoiInfoPanel (+ key / position for selection and map focus)
function toDetail(occ, language) {
  const when = [formatDay(occ.date, language), formatTime(occ)].filter(Boolean).join(" · ");
  return {
    key: occurrenceKey(occ),
    latitude: occ.latitude,
    longitude: occ.longitude,
    name: occ.name,
    description: occ.description ?? "",
    imageUrl: occ.image_url ?? null,
    url: occ.url ?? null,
    when: occ.is_cancelled ? `${when} (${t(TEXT.cancelled, language)})` : when,
  };
}

export default function EventPanel() {
  const { language, showPoints, selectItem, selectedItem, focusMap, setLoading } = useModuleContext();

  const [rangeDays, setRangeDays] = useState(RANGES[1].days);
  const [occurrences, setOccurrences] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const today = new Date();
    const from = isoDate(today);
    const to = isoDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() + rangeDays));

    let cancelled = false;
    setLoading(true);

    cachedLoad(`event-${from}-${to}-${language}`, () => api.events(from, to, language))
      .then((data) => {
        if (cancelled) return;
        setOccurrences(data);
        setLoaded(true);

        // one map point per event, even if it occurs on several days
        const seen = new Set();
        const unique = data.filter((o) => !seen.has(o.event_id) && seen.add(o.event_id));
        showPoints(normalizeToMapPoints(unique, "event"));
      })
      .catch((err) => console.error("Event load failed:", err))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [rangeDays, language, showPoints, setLoading]);

  // occurrences grouped by day, keeping the API's date/time order
  const days = useMemo(() => {
    const byDay = new Map();
    occurrences.forEach((o) => {
      if (!byDay.has(o.date)) byDay.set(o.date, []);
      byDay.get(o.date).push(o);
    });
    return [...byDay.entries()];
  }, [occurrences]);

  // keep an open detail panel in the current language
  useEffect(() => {
    if (!selectedItem?.key) return;
    const occ = occurrences.find((o) => occurrenceKey(o) === selectedItem.key);
    if (occ) selectItem(toDetail(occ, language));
  }, [occurrences]); // eslint-disable-line react-hooks/exhaustive-deps

  const openOccurrence = (occ) => {
    const detail = toDetail(occ, language);
    selectItem(detail);
    focusMap(detail);
  };

  return (
    <div className={styles["poi-page"]}>
      {/* Zeitraum */}
      <div className={styles.categories}>
        {RANGES.map((range) => (
          <button
            key={range.days}
            className={
              rangeDays === range.days
                ? `${styles.categoryBtn} ${styles.categoryBtnActive}`
                : styles.categoryBtn
            }
            onClick={() => setRangeDays(range.days)}
          >
            {t(range.label, language)}
          </button>
        ))}
      </div>

      {loaded && (
        <SideList>
          {!days.length && <SideListMessage>{t(TEXT.empty, language)}</SideListMessage>}

          {days.map(([day, items]) => (
            <SideListGroup
              key={day}
              header={
                <SideListDateHeader
                  {...dayHeaderParts(day, language)}
                  count={t(TEXT.count, language)(items.length)}
                />
              }
            >
              {items.map((occ) => (
                <SideListItem
                  key={occurrenceKey(occ)}
                  card
                  aside={timeAside(occ, language)}
                  title={occ.name}
                  meta={occ.description}
                  badge={occ.is_cancelled ? t(TEXT.cancelled, language) : null}
                  thumbUrl={occ.image_url}
                  muted={occ.is_cancelled}
                  active={selectedItem?.key === occurrenceKey(occ)}
                  onClick={() => openOccurrence(occ)}
                />
              ))}
            </SideListGroup>
          ))}
        </SideList>
      )}
    </div>
  );
}
